import type { ReactNode } from 'react';

const DEFAULT_COLUMN_WIDTH = 288;
const DEFAULT_MAX_COLUMNS = 4;

export type RowSpan = number | (Record<string, number> & { default?: number });

export interface MasonryWidget {
  id: string | number;
  colSpan: number;
  rowSpan: RowSpan;
  type?: string;
  widgetComponent?: ReactNode;
  [key: string]: unknown;
}

export interface LayoutMap {
  [position: string]: number;
}

export interface GenerateLayoutMapResult {
  layoutMap: LayoutMap;
  rowCount: number;
}

export interface MappedLayoutItem {
  key: string | number;
  widgetComponent?: ReactNode;
  colSpan: number;
  rowSpan: number;
}

interface AdjustedWidget {
  id: string | number;
  colSpan: number;
  rowSpan: number;
  type?: string;
  widgetComponent?: ReactNode;
  [key: string]: unknown;
}

interface RowWidgetEntry {
  widget: AdjustedWidget;
  startRow: number;
}

interface EmptyCell {
  row: number;
  col: number;
}

export const generateLayoutMap = (
  widgets: MasonryWidget[],
  columnCount: number,
  activeBreakpoint?: string,
): GenerateLayoutMapResult => {
  const layoutMap: LayoutMap = {};
  let maxRow = 0;

  const canPlaceWidget = (
    row: number,
    col: number,
    colSpan: number,
    rowSpan: number,
  ): boolean => {
    for (let r = 0; r < rowSpan; r++) {
      for (let c = 0; c < colSpan; c++) {
        if (layoutMap[`${row + r},${col + c}`] !== undefined) return false;
      }
    }

    return true;
  };

  const placeWidgetInMap = (
    row: number,
    col: number,
    colSpan: number,
    rowSpan: number,
    widgetIndex: number,
  ): void => {
    for (let r = 0; r < rowSpan; r++) {
      for (let c = 0; c < colSpan; c++) {
        layoutMap[`${row + r},${col + c}`] = widgetIndex;
      }
    }
    maxRow = Math.max(maxRow, row + rowSpan);
  };

  widgets.forEach((widget, index) => {
    const colSpan = Math.min(widget.colSpan, columnCount);
    const rowSpan =
      typeof widget.rowSpan === 'object'
        ? (activeBreakpoint && widget.rowSpan[activeBreakpoint]) ||
          widget.rowSpan.default ||
          1
        : widget.rowSpan;

    let placed = false;
    let row = 0;

    while (!placed) {
      for (let col = 0; col <= columnCount - colSpan; col++) {
        if (canPlaceWidget(row, col, colSpan, rowSpan)) {
          placeWidgetInMap(row, col, colSpan, rowSpan, index);
          placed = true;
          break;
        }
      }
      row++;
    }
  });

  return { layoutMap, rowCount: maxRow };
};

export const calculateColumnCount = (
  width: number,
  columnWidth: number = DEFAULT_COLUMN_WIDTH,
  maxColumns: number = DEFAULT_MAX_COLUMNS,
): number => Math.min(maxColumns, Math.floor(width / columnWidth));

export const mapLayout = (
  widgets: MasonryWidget[],
  layoutMap: LayoutMap,
  columnCount: number,
  rowCount: number,
  currentBreakpoint?: string,
): MappedLayoutItem[] => {
  const finalLayout: MappedLayoutItem[] = [];
  const renderedWidgets = new Set<string | number>();

  const widgetsCopy = adjustWidgets(widgets, columnCount, currentBreakpoint);

  for (let row = 0; row < rowCount; row++) {
    const { rowWidgets, emptyCells } = analyzeRow(
      layoutMap,
      widgetsCopy,
      row,
      columnCount,
    );

    if (emptyCells.length > 0) {
      expandWidgets(rowWidgets, emptyCells);
    }

    addRowWidgetsToLayout(rowWidgets, renderedWidgets, finalLayout);
  }

  return finalLayout;
};

const adjustWidgets = (
  widgets: MasonryWidget[],
  columnCount: number,
  currentBreakpoint?: string,
): AdjustedWidget[] =>
  widgets.map((widget) => ({
    ...widget,
    colSpan: Math.min(widget.colSpan, columnCount),
    rowSpan:
      typeof widget.rowSpan === 'object'
        ? (currentBreakpoint && widget.rowSpan[currentBreakpoint]) ||
          widget.rowSpan.default ||
          1
        : widget.rowSpan,
  }));

const analyzeRow = (
  layoutMap: LayoutMap,
  widgetsCopy: AdjustedWidget[],
  row: number,
  columnCount: number,
): { rowWidgets: RowWidgetEntry[]; emptyCells: EmptyCell[] } => {
  const rowWidgets: RowWidgetEntry[] = [];
  const emptyCells: EmptyCell[] = [];

  for (let col = 0; col < columnCount; col++) {
    const widgetIndex = layoutMap[`${row},${col}`];

    if (widgetIndex !== undefined) {
      const startRow = Math.min(
        ...Object.keys(layoutMap)
          .filter((key) => layoutMap[key] === widgetIndex)
          .map((key) => Number(key.split(',')[0])),
      );

      rowWidgets.push({ widget: widgetsCopy[widgetIndex], startRow });
    } else if (widgetIndex === undefined) {
      emptyCells.push({ row, col });
    }
  }

  return { rowWidgets, emptyCells };
};

const expandWidgets = (
  rowWidgets: RowWidgetEntry[],
  emptyCells: EmptyCell[],
): void => {
  for (const { widget, startRow } of rowWidgets) {
    if (
      widget.colSpan === 1 &&
      (widget.type === 'GOAL' || widget.type === 'RELEASE_NOTE')
    ) {
      const canExpand = emptyCells.some((empty) => empty.row === startRow);

      if (canExpand) {
        widget.colSpan += 1;
        emptyCells.splice(
          emptyCells.findIndex((empty) => empty.row === startRow),
          1,
        );
      }
    }
  }
};

const addRowWidgetsToLayout = (
  rowWidgets: RowWidgetEntry[],
  renderedWidgets: Set<string | number>,
  finalLayout: MappedLayoutItem[],
): void => {
  rowWidgets.forEach(({ widget }) => {
    if (!renderedWidgets.has(widget.id)) {
      renderedWidgets.add(widget.id);
      finalLayout.push({
        key: widget.id,
        widgetComponent: widget.widgetComponent,
        colSpan: widget.colSpan,
        rowSpan: widget.rowSpan,
      });
    }
  });
};
